import * as vp from 'vopay';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useFetchAccountUsers from '../../../hooks/useApi/useFetchAccountUsers';

import usePostDeleteAccountUser from '../../../hooks/useApi/usePostDeleteAccountUser';
import usePostAccountUsersChangePassword, {
  ChangePasswordRequest,
} from '../../../hooks/useApi/usePostAccountUsersChangePassword';
import usePostCreateAccountUser from '../../../hooks/useApi/usePostCreateAccountUser';
import UsersContext from './UsersContext';
import UsersDialogue from './UsersDialogue';
import type { UsersCtx } from './UsersContext';
import AddUser from './AddUser';
import ChangePassword from './ChangePassword';
import UserList from './UsersList';
import AccountUserPermissions from './AccountUserPermissionsContainer';
import { UserDialogueHandlers, DialogueHandlerProps } from '../types';
import { PendingDataProvider, usePendingData } from '../../../providers/PendingData.provider';
import usePostAccountUserPermissions from '../../../hooks/useApi/usePostAccountUserPermissions';
import useFetchAccountUserPermissions from '../../../hooks/useApi/useFetchAccountUserPermissions';
import usePermissionUpdateDialogue from '../../../hooks/useDialogues/usePermissionUpdate.dialogue';

type AccountUserType = {
  Username: string;
  'Date Added'?: string;
  UserType: string;
};

type UsersContainerProps = {
  details: vp.AccountDetailsMap;
};
type PageContents = {
  userList: React.JSX.Element;
  createAccount: React.JSX.Element;
  changePassword: React.JSX.Element;
  changeAccountUserPermission: React.JSX.Element;
};

type FC = FunctionComponent<UsersContainerProps>;

const UsersContainer: FC = ({ details }: UsersContainerProps) => {
  const { accountId } = useParams<{ accountId: string }>();
  const [DeleteResponse, deleteAccountUser] = usePostDeleteAccountUser({ bearer: details[accountId].token });
  const [AccountUsers, getAccountUsers] = useFetchAccountUsers({ bearer: details[accountId].token });
  const [CreateAccountUserResponse, createAccountUser] = usePostCreateAccountUser({ bearer: details[accountId].token });

  const [AccountUsersChangePasswordResponse, setAccountUsersChangePassword] = usePostAccountUsersChangePassword({
    bearer: details[accountId].token,
  });
  const [isDeleteUser, setIsDeleteUser] = useState(false);
  const [accounts, setAccounts] = useState<AccountUserType[]>([]);
  const [shouldFetchUsers, setFetchUsers] = useState(true);
  const [shouldChangePassword, setShouldChangePassword] = useState(false);
  const [shouldCreateUser, setShouldCreateUser] = useState(false);
  const [activePage, setActivePage] = useState<keyof PageContents>('userList');
  const [changePasswordInitialData, setChangePasswordInitialData] = useState<vp.AccountUsersChangePasswordRequest>({
    EmailAddress: '',
    NewPassword: '',
    SendEmail: true,
  });
  const [deleteUserData, setDeleteUserData] = useState<vp.DeleteAccountUserRequest>({
    EmailAddress: '',
  });
  const [userDeleteError, setUserDeleteError] = useState('');
  const [showUserDeleteErrorAlert, setShowUserDeleteErrorAlert] = useState(false);
  const [showUserDeleteSuccessAlert, setShowUserDeleteSuccessAlert] = useState(false);
  const [userCreateError, setUserCreateError] = useState('');
  const [showUserCreateErrorAlert, setShowUserCreateErrorAlert] = useState(false);
  const [showUserCreateSuccessAlert, setShowUserCreateSuccessAlert] = useState(false);
  const [userChangePasswordError, setUserChangePasswordError] = useState('');
  const [showUserChangePasswordErrorAlert, setShowUserChangePasswordErrorAlert] = useState(false);
  const [showUserChangePasswordSuccessAlert, setShowUserChangePasswordSuccessAlert] = useState(false);
  const [showUserDeleteConfirmationAlert, setShowUserDeleteConfirmationAlert] = useState(false);
  const [showUserChangePasswordConfirmationAlert, setShowUserChangePasswordConfirmationAlert] = useState(false);
  const [accountUser, setAccountUser] = useState('');
  const [mappedAccountUserPermissions, setMappedAccountUserPermissions] = useState<vp.AccountUserPermissionsMap>({});
  const [accountUserEmailAddress, setAccountUsersEmailAddress] = useState('');
  const { pendingData, updatePendingData, initPendingData, reset } = usePendingData();
  const [accountUserPermissions, getAccountUserPermissions] = useFetchAccountUserPermissions({
    bearer: details[accountId].token,
  });
  const [accountUserPermissionsResponse, postAccountUserPermissions] = usePostAccountUserPermissions({
    bearer: details[accountId].token,
  });
  const { showDialogue: showPermissionsDialogue } = usePermissionUpdateDialogue(accountUserPermissionsResponse);

  const history = useHistory();
  const DefaultAddUser: vp.CreateAccountUserRequest = {
    EmailAddress: '',
    SendEmail: true,
  };
  const usersContext: UsersCtx = {
    userDeleteError,
    showUserDeleteErrorAlert,
    showUserDeleteSuccessAlert,
    userCreateError,
    showUserCreateErrorAlert,
    showUserCreateSuccessAlert,
    userChangePasswordError,
    showUserChangePasswordErrorAlert,
    showUserChangePasswordSuccessAlert,
    showUserDeleteConfirmationAlert,
    showUserChangePasswordConfirmationAlert,
    accountUser,
    accountUserEmailAddress,
    accountUserPermissions,
  };

  useEffect(() => {
    if (!shouldFetchUsers) return;
    getAccountUsers({});
  }, [shouldFetchUsers]);

  useEffect(() => {
    const { Users } = AccountUsers;
    setFetchUsers(false);
    if (Users) {
      const UsersList = Object.values(Users);
      setAccounts(UsersList);
    }
  }, [AccountUsers]);
  useEffect(() => {
    if (shouldChangePassword === true) {
      const { success, errorMessage } = AccountUsersChangePasswordResponse;

      if (success === true) {
        setShowUserChangePasswordSuccessAlert(true);
      } else {
        setUserChangePasswordError(errorMessage);
        setShowUserChangePasswordErrorAlert(true);
      }
      setShouldChangePassword(false);
    }
  }, [AccountUsersChangePasswordResponse]);
  useEffect(() => {
    if (shouldCreateUser === true) {
      const { Success, ErrorMessage } = CreateAccountUserResponse;

      if (Success === true) {
        setShowUserCreateSuccessAlert(true);
      } else {
        setUserCreateError(ErrorMessage);
        setShowUserCreateErrorAlert(true);
      }
      setShouldCreateUser(false);
    }
  }, [CreateAccountUserResponse]);

  useEffect(() => {
    if (isDeleteUser) {
      const { Success, ErrorMessage } = DeleteResponse;
      if (Success === true) {
        setShowUserDeleteSuccessAlert(true);
      } else {
        setUserDeleteError(ErrorMessage);
        setShowUserDeleteErrorAlert(true);
      }
      setIsDeleteUser(false);
    }
  }, [DeleteResponse]);

  useEffect(() => {
    const AccountUserMap = Object.values(accountUserPermissions.Permissions || {}).reduce(
      (obj, item) => Object.assign(obj, { [item]: true }),
      {},
    ) as vp.AccountUserPermissionsMap;
    setMappedAccountUserPermissions(AccountUserMap);
  }, [accountUserPermissions]);

  useEffect(() => {
    const updatePermissions = () => {
      if (accountUserPermissionsResponse.success || accountUserPermissionsResponse.errorMessage)
        showPermissionsDialogue();
    };
    updatePermissions();
  }, [accountUserPermissionsResponse]);

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<UserDialogueHandlers>) => {
      const { type, state, action = 'default', hasNavigation } = handlerProps;

      const dialogueHandlers: UserDialogueHandlers = {
        remove: {
          success: {
            refresh: () => {
              setFetchUsers(true);
              setShowUserDeleteSuccessAlert(false);

              setActivePage('userList');
            },
            default: () => {
              setShowUserDeleteSuccessAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowUserDeleteErrorAlert(false);
              setUserDeleteError('');
            },
          },
        },
        create: {
          success: {
            refresh: () => {
              setFetchUsers(true);

              setShowUserCreateSuccessAlert(false);

              setActivePage('userList');
            },
            default: () => {
              setShowUserCreateSuccessAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowUserCreateErrorAlert(false);
              setUserCreateError('');
            },
          },
        },
        change: {
          success: {
            refresh: () => {
              setFetchUsers(true);

              setShowUserChangePasswordSuccessAlert(false);
              setActivePage('userList');
            },
            default: () => {
              setShowUserChangePasswordSuccessAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowUserChangePasswordErrorAlert(false);
              setUserChangePasswordError('');
            },
          },
        },
        alertPassword: {
          success: {
            default: () => {
              setShouldChangePassword(true);

              const requestPassword: ChangePasswordRequest = {
                emailAddress: changePasswordInitialData.EmailAddress,
                newPassword: changePasswordInitialData.NewPassword,
                shouldSendWelcomeEmail: changePasswordInitialData.SendEmail,
              };
              setAccountUsersChangePassword(requestPassword);
              setShowUserChangePasswordConfirmationAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowUserChangePasswordConfirmationAlert(false);
            },
          },
        },
        alertDelete: {
          success: {
            default: () => {
              setShowUserDeleteConfirmationAlert(false);
              setIsDeleteUser(true);
              deleteAccountUser(deleteUserData);
            },
          },
          fail: {
            default: () => {
              setShowUserDeleteConfirmationAlert(false);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();

      if (hasNavigation) history.push('/accounts');
    },
    onChangePassword: (values: vp.AccountUsersChangePasswordRequest) => {
      setChangePasswordInitialData(values);
      setShowUserChangePasswordConfirmationAlert(true);
    },
    onAddUser: (values: vp.CreateAccountUserRequest) => {
      setShouldCreateUser(true);
      createAccountUser(values);
    },
    onBack: () => {
      setActivePage('userList');
    },
    onAddUserPage: () => {
      setActivePage('createAccount');
    },
    onChangePasswordPage: (values: vp.AccountUsersChangePasswordRequest) => {
      setChangePasswordInitialData(values);
      setAccountUser(values.EmailAddress);
      setActivePage('changePassword');
    },
    onDeleteUser: (values: vp.DeleteAccountUserRequest) => {
      setAccountUser(values.EmailAddress);
      setShowUserDeleteConfirmationAlert(true);
      setDeleteUserData(values);
    },
    onAccountUserPermissionPage: (values: vp.AccountUserPermissionsRequest) => {
      setAccountUser(values.EmailAddress);
      setAccountUsersEmailAddress(values.EmailAddress);
      getAccountUserPermissions(values);
      setActivePage('changeAccountUserPermission');
    },
    initializeAccountUserPermissions: (value: vp.AccountUserPermissions) => {
      initPendingData(value);
    },
    onAccountUserPermissionChange: (permissionId: vp.AccountUserPermissionsId, permissionValue: string | boolean) => {
      updatePendingData({ [permissionId]: permissionValue });
    },
    onAccountUserPermissionSave: (emailAddress: string) => {
      const params = { EmailAddress: emailAddress, ...(pendingData as vp.AccountUserPermissions) };
      postAccountUserPermissions(params);
      reset();
    },
  };

  return (
    <UsersContext.Provider value={usersContext}>
      {activePage === 'createAccount' && <AddUser handlers={handlers} defaultValues={DefaultAddUser} />}
      {activePage === 'changePassword' && (
        <ChangePassword defaultValues={changePasswordInitialData} handlers={handlers} />
      )}
      {activePage === 'changeAccountUserPermission' && (
        <PendingDataProvider>
          <AccountUserPermissions
            accountUserEmailAddress={accountUserEmailAddress}
            accountUserPermissions={mappedAccountUserPermissions}
            handlers={handlers}
          />
        </PendingDataProvider>
      )}
      {activePage === 'userList' && <UserList handlers={handlers} accounts={accounts} />}
      <UsersDialogue handleDialogue={handlers.handleDialogue} />
    </UsersContext.Provider>
  );
};

export default UsersContainer;
